/**
 * Production Order Service
 *
 * Handles all production order operations including:
 * - Production order CRUD and lifecycle
 * - Status transitions
 * - Material issue and return
 * - Finished goods receipt
 * - Work center management
 * - Sales order integration
 */

import httpClient from './httpClient';

const productionService = {
  // ==================== Production Orders - List & Search ====================

  /**
   * Get list of production orders with filtering and pagination
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default 1)
   * @param {number} params.page_size - Items per page (default 20)
   * @param {string} params.search - Search term
   * @param {string} params.status - Filter by status
   * @param {string} params.work_center_id - Filter by work center
   * @param {string} params.source_id - Filter by source (e.g., sales order)
   * @param {string} params.client_id - Filter by client
   */
  async getProductionOrders(params = {}) {
    try {
      const response = await httpClient.get('/api/production-orders', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching production orders:', error);
      throw error;
    }
  },

  /**
   * Get a single production order by ID
   * @param {string} orderId - Production order ID
   */
  async getProductionOrder(orderId) {
    try {
      const response = await httpClient.get(`/api/production-orders/${orderId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching production order:', error);
      throw error;
    }
  },

  /**
   * Get production statistics for dashboard
   */
  async getProductionStats() {
    try {
      const response = await httpClient.get('/api/production-orders/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching production stats:', error);
      throw error;
    }
  },

  // ==================== Production Orders - CRUD ====================

  /**
   * Create a new production order
   * @param {Object} orderData - Order data
   * @param {string} orderData.bom_id - BOM ID (required)
   * @param {number} orderData.planned_quantity - Quantity to produce (required)
   * @param {string} orderData.priority - low, normal, high, urgent
   * @param {string} orderData.source_type - 'sales_order' or null
   * @param {string} orderData.source_id - Source document ID
   * @param {string} orderData.work_center_id - Assigned work center
   * @param {string} orderData.planned_start_date - Planned start date
   * @param {string} orderData.planned_end_date - Planned end date
   * @param {string} orderData.notes - Optional notes
   */
  async createProductionOrder(orderData) {
    try {
      const response = await httpClient.post('/api/production-orders', orderData);
      return response.data;
    } catch (error) {
      console.error('Error creating production order:', error);
      throw error;
    }
  },

  /**
   * Update a production order
   * @param {string} orderId - Production order ID
   * @param {Object} updates - Fields to update
   */
  async updateProductionOrder(orderId, updates) {
    try {
      const response = await httpClient.put(`/api/production-orders/${orderId}`, updates);
      return response.data;
    } catch (error) {
      console.error('Error updating production order:', error);
      throw error;
    }
  },

  // ==================== Status Management ====================

  /**
   * Release a production order for production
   * @param {string} orderId - Production order ID
   */
  async releaseOrder(orderId) {
    try {
      const response = await httpClient.post(`/api/production-orders/${orderId}/release`);
      return response.data;
    } catch (error) {
      console.error('Error releasing production order:', error);
      throw error;
    }
  },

  /**
   * Start production on an order
   * @param {string} orderId - Production order ID
   */
  async startProduction(orderId) {
    try {
      const response = await httpClient.post(`/api/production-orders/${orderId}/start`);
      return response.data;
    } catch (error) {
      console.error('Error starting production:', error);
      throw error;
    }
  },

  /**
   * Update production progress
   * @param {string} orderId - Production order ID
   * @param {Array} updates - Array of item updates [{ item_id, completed_quantity, scrap_quantity, bars_consumed }]
   * @param {string} notes - Optional notes
   */
  async updateProgress(orderId, updates, notes = '') {
    try {
      const response = await httpClient.post(`/api/production-orders/${orderId}/progress`, {
        updates,
        notes
      });
      return response.data;
    } catch (error) {
      console.error('Error updating production progress:', error);
      throw error;
    }
  },

  /**
   * Complete production on an order
   * @param {string} orderId - Production order ID
   * @param {number} completedQty - Quantity of finished goods produced
   * @param {number} scrappedQty - Quantity scrapped during production
   * @param {string} notes - Completion notes
   * @param {number} actualMaterialsUsed - Actual materials consumed (optional)
   * @param {Array} remainingBars - Array of remaining bar entries [{ material_code, length, quantity, location, product_id }]
   * @param {Array} productQuantities - Per-product completion quantities
   * @param {string} defaultLocation - Default warehouse location for finished goods
   * @param {string} leftoverOverrideJustification - Reason for overriding calculated leftovers (optional)
   */
  async completeProduction(orderId, completedQty, scrappedQty, notes = '', actualMaterialsUsed = null, remainingBars = [], productQuantities = [], defaultLocation = 'main', leftoverOverrideJustification = '') {
    try {
      const response = await httpClient.post(`/api/production-orders/${orderId}/complete`, {
        completed_qty: completedQty,
        scrapped_qty: scrappedQty,
        notes,
        actual_materials_used: actualMaterialsUsed,
        remaining_bar_data: (remainingBars || []).map(bar => ({
          length: bar.length,
          quantity: bar.quantity,
          location: bar.location,
          product_id: bar.product_id || bar.material_code,
          material_code: bar.material_code,
          leftover_type: bar.leftover_type || 'blank',
          tip_diameter: bar.tip_diameter
        })),
        product_quantities: productQuantities,
        finished_goods_location: defaultLocation,
        leftover_override_justification: leftoverOverrideJustification
      });
      return response.data;
    } catch (error) {
      console.error('Error completing production:', error);
      throw error;
    }
  },

  /**
   * Cancel a production order
   * @param {string} orderId - Production order ID
   * @param {string} reason - Cancellation reason
   */
  async cancelOrder(orderId, reason = '') {
    try {
      const response = await httpClient.post(`/api/production-orders/${orderId}/cancel`, { reason });
      return response.data;
    } catch (error) {
      console.error('Error cancelling production order:', error);
      throw error;
    }
  },

  // ==================== Material Operations ====================

  /**
   * Issue materials to a production order
   * @param {string} orderId - Production order ID
   * @param {Array} materials - Materials to issue with per-item locations
   *   Format: [{ material_product_id, material_product_code, quantity, location }]
   */
  async issueMaterials(orderId, materials) {
    try {
      const response = await httpClient.post(`/api/production-orders/${orderId}/issue-materials`, {
        materials
      });
      return response.data;
    } catch (error) {
      console.error('Error issuing materials:', error);
      throw error;
    }
  },

  /**
   * Return unused materials from a production order
   * @param {string} orderId - Production order ID
   * @param {Array} materials - Materials to return
   * @param {string} location - Target warehouse location
   */
  async returnMaterials(orderId, materials, location = 'main') {
    try {
      const response = await httpClient.post(`/api/production-orders/${orderId}/return-materials`, {
        materials,
        location
      });
      return response.data;
    } catch (error) {
      console.error('Error returning materials:', error);
      throw error;
    }
  },

  /**
   * Record additional leftover bars for an already-completed production order
   * @param {string} orderId - Production order ID
   * @param {Array} leftoverBars - Array of leftover bar entries
   * @param {string} justification - Reason for recording additional leftovers
   */
  async recordAdditionalLeftover(orderId, leftoverBars, justification = '') {
    try {
      const response = await httpClient.post(`/api/production-orders/${orderId}/record-leftover`, {
        leftover_bars: leftoverBars,
        justification
      });
      return response.data;
    } catch (error) {
      console.error('Error recording additional leftover:', error);
      throw error;
    }
  },

  /**
   * Suggest available leftovers for a BOM's materials
   * @param {string} bomId - BOM ID
   * @param {number} plannedQuantity - Planned production quantity
   * @returns {Object} Leftover suggestions including available leftovers per material
   */
  async suggestLeftovers(bomId, plannedQuantity) {
    try {
      const response = await httpClient.post('/api/production-orders/suggest-leftovers', {
        bom_id: bomId,
        planned_quantity: plannedQuantity
      });
      return response.data;
    } catch (error) {
      console.error('Error suggesting leftovers:', error);
      throw error;
    }
  },

  async getProductStock(productId) {
    try {
      const response = await httpClient.get(`/api/products/${productId}/stock`);
      return response.data.quantity || 0;
    } catch (error) {
      console.error('Error getting product stock:', error);
      return 0;
    }
  },

  async getProductStockByLocation(productId) {
    try {
      // Use the existing /stock endpoint which already returns stock_by_location
      const response = await httpClient.get(`/api/products/${productId}/stock`);
      const data = response.data || {};
      return {
        stock_by_location: data.stock_by_location || [],
        total_stock: data.quantity || 0
      };
    } catch (error) {
      console.error('Error getting product stock by location:', error);
      return { stock_by_location: [], total_stock: 0 };
    }
  },

  /**
   * Receive finished goods from production (additional receipt after completion)
   * @param {string} orderId - Production order ID
   * @param {Array} items - Items to receive [{item_index, quantity}]
   * @param {string} location - Storage location
   * @param {string} notes - Optional notes/reason
   */
  async additionalReceipt(orderId, items, location = 'main', notes = '') {
    try {
      const response = await httpClient.post(`/api/production-orders/${orderId}/receive-finished`, {
        items,
        location,
        notes
      });
      return response.data;
    } catch (error) {
      console.error('Error recording additional receipt:', error);
      throw error;
    }
  },

  /**
   * Receive partial leftover bars from an in-progress production order
   * @param {string} orderId - Production order ID
   * @param {Array} leftoverBars - Leftover bars to receive
   * @param {string} notes - Optional notes/reason
   */
  async receivePartialLeftover(orderId, leftoverBars, notes = '') {
    try {
      const response = await httpClient.post(`/api/production-orders/${orderId}/receive-leftover`, {
        leftover_bars: leftoverBars,
        notes
      });
      return response.data;
    } catch (error) {
      console.error('Error receiving partial leftover:', error);
      throw error;
    }
  },

  // ==================== Material Availability ====================

  /**
   * Check material availability for a production order
   * @param {string} orderId - Production order ID
   */
  async checkMaterialAvailability(orderId) {
    try {
      const response = await httpClient.get(`/api/production-orders/${orderId}/check-availability`);
      return response.data;
    } catch (error) {
      console.error('Error checking material availability:', error);
      throw error;
    }
  },

  /**
   * Get material tracking status for a production order
   * @param {string} orderId - Production order ID
   */
  async getMaterialStatus(orderId) {
    try {
      const response = await httpClient.get(`/api/production-orders/${orderId}/material-status`);
      return response.data;
    } catch (error) {
      console.error('Error getting material status:', error);
      throw error;
    }
  },

  /**
   * Update progress for a specific process step
   * @param {string} orderId - Production order ID
   * @param {number} itemIndex - Item index in the order
   * @param {number} stepSequence - Step sequence number
   * @param {number} quantity - Quantity completed
   * @param {number} scrapQuantity - Quantity scrapped (optional)
   */
  async updateStepProgress(orderId, itemIndex, stepSequence, quantity, scrapQuantity = 0) {
    try {
      const response = await httpClient.post(
        `/api/production-orders/${orderId}/items/${itemIndex}/steps/${stepSequence}/complete`,
        { quantity, scrap_quantity: scrapQuantity }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating step progress:', error);
      throw error;
    }
  },

  /**
   * Refresh process steps for a production order item from current BOM template
   * @param {string} orderId - Production order ID
   * @param {number} itemIndex - Item index (optional, refreshes all if not provided)
   */
  async refreshProcessSteps(orderId, itemIndex = null) {
    try {
      const url = itemIndex !== null
        ? `/api/production-orders/${orderId}/refresh-process-steps?item_index=${itemIndex}`
        : `/api/production-orders/${orderId}/refresh-process-steps`;
      const response = await httpClient.post(url);
      return response.data;
    } catch (error) {
      console.error('Error refreshing process steps:', error);
      throw error;
    }
  },

  /**
   * Get material issues for a production order
   * @param {string} orderId - Production order ID
   * @returns {Promise<Object>} Map of material code to issued quantity
   */
  async getMaterialIssues(orderId) {
    try {
      const response = await httpClient.get(`/api/production-orders/${orderId}/material-issues`);
      return response.data;
    } catch (error) {
      console.error('Error fetching material issues:', error);
      throw error;
    }
  },

  /**
   * Calculate ideal bar consumption for a production order
   * @param {string} orderId - Production order ID
   * @param {number} scrapRate - Additional scrap rate (0-1)
   * @returns {Promise<Object>} Bar calculation results
   */
  async calculateIdealBars(orderId, scrapRate = 0) {
    try {
      const response = await httpClient.get(`/api/production-orders/${orderId}/calculate-ideal`, {
        params: { scrap_rate: scrapRate }
      });
      return response.data;
    } catch (error) {
      console.error('Error calculating ideal bars:', error);
      throw error;
    }
  },

  // ==================== Work Centers ====================

  /**
   * Get list of work centers
   * @param {Object} params - Query parameters
   * @param {string} params.status - Filter by status (active, maintenance, etc.)
   * @param {string} params.type - Filter by work center type
   * @returns {Promise<Object>} List of work centers
   */
  async getWorkCenters(params = {}) {
    try {
      const response = await httpClient.get('/api/work-centers', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching work centers:', error);
      throw error;
    }
  },

  /**
   * Create a new work center
   * @param {Object} data - Work center data
   * @returns {Object} Created work center
   */
  async createWorkCenter(data) {
    try {
      const response = await httpClient.post('/api/work-centers', data);
      return response.data;
    } catch (error) {
      console.error('Error creating work center:', error);
      throw error;
    }
  },

  /**
   * Update a work center
   * @param {string} id - Work center _id or work_center_id
   * @param {Object} data - Update data
   * @returns {Object} Updated work center
   */
  async updateWorkCenter(id, data) {
    try {
      const response = await httpClient.put(`/api/work-centers/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating work center:', error);
      throw error;
    }
  },

  /**
   * Delete a work center
   * @param {string} id - Work center _id or work_center_id
   * @returns {Object} Result
   */
  async deleteWorkCenter(id) {
    try {
      const response = await httpClient.delete(`/api/work-centers/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting work center:', error);
      throw error;
    }
  },

  /**
   * Get work center types and statuses
   * @returns {Object} Types and statuses
   */
  async getWorkCenterTypes() {
    try {
      const response = await httpClient.get('/api/work-centers/types');
      return response.data;
    } catch (error) {
      console.error('Error fetching work center types:', error);
      throw error;
    }
  },

  /**
   * Get BOM materials for a production order
   * @param {string} orderId - Production order ID
   * @returns {Object} BOM materials list
   */
  async getBOMMaterials(orderId) {
    try {
      const response = await httpClient.get(`/api/production-orders/${orderId}/bom-materials`);
      return response.data;
    } catch (error) {
      console.error('Error fetching BOM materials:', error);
      throw error;
    }
  },

  /**
   * Get available warehouse locations for materials
   * @returns {Object} List of warehouse locations
   */
  async getMaterialWarehouseLocations() {
    try {
      const response = await httpClient.get('/api/materials/warehouse-locations');
      return response.data;
    } catch (error) {
      console.error('Error fetching warehouse locations:', error);
      throw error;
    }
  },

  /**
   * Add material issue to a production order
   * @param {string} orderId - Production order ID
   * @param {Array} materials - Materials to issue
   * @returns {Object} Result
   */
  async addMaterialIssue(orderId, payload) {
    try {
      // Support both old format (materials array) and new format (object with materials, notes, attachments)
      const data = Array.isArray(payload) ? { materials: payload } : payload;
      const response = await httpClient.post(`/api/production-orders/${orderId}/issue-materials`, data);
      return response.data;
    } catch (error) {
      console.error('Error adding material issue:', error);
      throw error;
    }
  },

  /**
   * AI-powered material suggestion for a production order
   * @param {string} orderId - Production order ID
   * @returns {Object} AI suggestions for material quantities
   */
  async suggestMaterials(orderId) {
    try {
      const response = await httpClient.post(`/api/production-orders/${orderId}/suggest-materials`);
      return response.data;
    } catch (error) {
      console.error('Error suggesting materials:', error);
      throw error;
    }
  },

  // ==================== BOM Assignment (Draft Orders) ====================

  async assignBomToItem(orderId, itemIndex, bomId) {
    try {
      const response = await httpClient.post(
        `/api/production-orders/${orderId}/items/${itemIndex}/assign-bom`,
        { bom_id: bomId }
      );
      return response.data;
    } catch (error) {
      console.error('Error assigning BOM to item:', error);
      throw error;
    }
  },

  async removeItemFromOrder(orderId, itemIndex) {
    try {
      const response = await httpClient.delete(
        `/api/production-orders/${orderId}/items/${itemIndex}`
      );
      return response.data;
    } catch (error) {
      console.error('Error removing item from order:', error);
      throw error;
    }
  },

  async updateOrderItems(orderId, items) {
    try {
      const response = await httpClient.put(`/api/production-orders/${orderId}/items`, {
        items
      });
      return response.data;
    } catch (error) {
      console.error('Error updating order items:', error);
      throw error;
    }
  },

  /**
   * Get unassigned production orders (not linked to any sales order)
   * @param {number} page - Page number
   * @param {number} pageSize - Items per page
   * @param {Object} filters - Filter options
   * @param {string} filters.client_id - Filter by client ID
   */
  async getUnassignedProductionOrders(page = 1, pageSize = 20, filters = {}) {
    try {
      const params = {
        page,
        page_size: pageSize,
        ...(filters.client_id && { client_id: filters.client_id }),
      };
      const response = await httpClient.get('/api/production-orders/unassigned', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching unassigned production orders:', error);
      throw error;
    }
  },

  /**
   * Link existing production orders to a sales order
   * @param {string} salesOrderId - Sales order MongoDB ObjectId
   * @param {Array} prdAllocations - Array of PRD allocations with item mappings
   */
  async linkProductionOrdersToSalesOrder(salesOrderId, prdAllocations) {
    try {
      const response = await httpClient.post(
        `/api/sales-orders/${salesOrderId}/link-production-orders`,
        { prd_allocations: prdAllocations }
      );
      return response.data;
    } catch (error) {
      console.error('Error linking production orders to sales order:', error);
      throw error;
    }
  },

  /**
   * Unlink a production order from its source sales order
   * @param {string} productionOrderId - Production order ID
   */
  async unlinkProductionOrderFromSales(productionOrderId) {
    try {
      const response = await httpClient.post(`/api/production-orders/${productionOrderId}/unlink-from-sales`);
      return response.data;
    } catch (error) {
      console.error('Error unlinking production order from sales:', error);
      throw error;
    }
  },

  /**
   * Delete a production order permanently
   * @param {string} orderId - Production order ID
   */
  async deleteProductionOrder(orderId) {
    try {
      const response = await httpClient.delete(`/api/production-orders/${orderId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting production order:', error);
      throw error;
    }
  }
};

export default productionService;
